import { useCallback, useEffect, useRef, useState } from 'react'
import { StatusCodes } from 'http-status-codes'
import { toEstimatePayload } from '@/features/consignment-form/api-payload'
import { estimateErrorSchema, estimateSchema, retryAfterTime } from '@/features/consignment-form/contract'
import { estimateKey } from '@/features/consignment-form/estimate'
import type { EstimateState, EstimateStatus } from '@/features/consignment-form/estimate'
import type { Product } from '@/features/consignment-form/products'
import type { Item } from '@/features/consignment-form/schema'
import { postJson } from '@/lib/api/browser'
import { localApi } from '@/lib/api/endpoints'

function overallStatus(saved: readonly (EstimateState | undefined)[]): EstimateStatus {
  if (saved.length === 0) return 'empty'
  if (saved.some((estimate) => !estimate || estimate.status === 'loading')) return 'loading'
  if (saved.some((estimate) => estimate?.status === 'unavailable')) return 'unavailable'
  return 'ready'
}

/**
 * One estimate per distinct row, fetched once and kept under the row's content. A row that
 * failed is not asked again on its own; the user retries it.
 */
export function useEstimates(products: Product[] | null, rows: readonly Item[]) {
  const [estimates, setEstimates] = useState<Record<string, EstimateState>>({})
  const requests = useRef(new Map<string, Promise<EstimateState>>())

  /** Asks for the item's estimate and answers with how it ended, so a save can wait for a price. */
  const estimateItem = useCallback(
    (item: Item): Promise<EstimateState> => {
      const key = estimateKey(item)
      const active = requests.current.get(key)
      if (active) return active

      const settle = (state: EstimateState) => {
        setEstimates((current) => ({ ...current, [key]: state }))
        return state
      }

      let body: ReturnType<typeof toEstimatePayload>
      try {
        body = toEstimatePayload(item, products ?? [])
      } catch {
        return Promise.resolve(settle({ status: 'unavailable', reason: 'invalid_item' }))
      }
      setEstimates((current) => ({ ...current, [key]: { status: 'loading' } }))
      const task = (async (): Promise<EstimateState> => {
        try {
          const response = await postJson(localApi.estimate, JSON.stringify(body))
          if (!response.ok) {
            const error = estimateErrorSchema.safeParse(await response.json().catch(() => null))
            const reason =
              response.status === StatusCodes.TOO_MANY_REQUESTS
                ? 'rate_limited'
                : error.success && error.data.error === 'estimate_access_unavailable'
                  ? 'access'
                  : 'temporary'
            const retryAt = reason === 'rate_limited' ? retryAfterTime(response.headers.get('Retry-After')) : undefined
            const backend = error.success ? error.data.backend : undefined
            return settle({ status: 'unavailable', reason, retryAt, backend })
          }
          return settle({ status: 'ready', value: estimateSchema.parse(await response.json()) })
        } catch {
          return settle({ status: 'unavailable', reason: 'temporary' })
        } finally {
          requests.current.delete(key)
        }
      })()
      requests.current.set(key, task)
      return task
    },
    [products]
  )

  /* oxlint-disable react/set-state-in-effect -- Rows restored from a draft are priced by the API; the loading marker is what stops a second request. */
  useEffect(() => {
    if (!products) return
    for (const row of rows) {
      if (estimates[estimateKey(row)] === undefined) void estimateItem(row)
    }
  }, [rows, estimates, estimateItem, products])
  /* oxlint-enable react/set-state-in-effect */

  const saved = rows.map((row) => estimates[estimateKey(row)])
  const sum = saved.reduce(
    (acc, estimate) => acc + (estimate?.status === 'ready' ? estimate.value.estimatedAmount : 0),
    0
  )
  // Row amounts carry two decimals; their floating-point sum must not show a third.
  const total = Math.round(sum * 100) / 100

  return { estimates, estimateItem, status: overallStatus(saved), total }
}
